import React, { type SyntheticEvent, useCallback, useEffect, useRef, useState } from "react";
import { Box, Image, Skeleton, Stack, Text } from "@chakra-ui/react";
import { colors, lightColorWithDarkTheme, lightThemeColorValues } from "../constants/GlobalStyles";
import {
  CardData,
  CardType,
  MAX_WIDTH_FOR_ANSWER_AND_FEEDBACK_CONTENT,
} from "../constants/GlobalTypesAndEnumsAndConstants";
import { addSentryDiagnosticBreadcrumb, trackError } from "../utils/analytics/track-error";
import { useDisplayableImageUrl } from "../contextsAndHooks/useDisplayableImageUrl";

const IMAGE_OCCLUSION_ASSET_SCOPE = "image-occlusion-asset";

// The label a question asks about. On the light theme it is a lime chip among pale grey masks. On a dark
// theme the masks are dark slabs on the white picture, and a dark green chip would be one more of them, so
// the chip is the theme's bright green with dark ink.
// The learner's picture is a white sheet in every theme. The covers the app draws over its labels keep the
// theme's dark colors: a dark cover reads as hiding something.
const ASKED_ABOUT_LABEL_FILL = lightColorWithDarkTheme("#D4FDB7", colors.primaryAction.default);
const ASKED_ABOUT_LABEL_INK = lightColorWithDarkTheme(lightThemeColorValues.text.body, colors.surface.primary);

type ImageAssetOutcome = "load" | "load-error" | "missing-src";

function getStableObjectMarker(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function getNonNetworkImageSourceMetadata(scheme: string) {
  return {
    present: true,
    scheme,
    host: null,
    objectMarker: getStableObjectMarker(`non-network:${scheme}`),
    hasQuery: false,
    validUrl: true,
    urlWithoutCredentials: null,
  };
}

function getSafeImageSourceMetadata(source: string | undefined) {
  const sourceScheme = source
    ?.slice(0, 64)
    .trimStart()
    .match(/^([a-z][a-z\d+.-]*):/i)?.[1]
    ?.toLowerCase();
  // Classify explicit non-network schemes before URL parsing so inline payloads never enter diagnostics.
  const isExplicitNonNetworkScheme = sourceScheme && sourceScheme !== "http" && sourceScheme !== "https";
  if (isExplicitNonNetworkScheme) {
    return getNonNetworkImageSourceMetadata(sourceScheme);
  }

  const trimmedSource = source?.trim();
  if (!trimmedSource) {
    return {
      present: false,
      scheme: null,
      host: null,
      objectMarker: null,
      hasQuery: false,
      validUrl: false,
      urlWithoutCredentials: null,
    };
  }

  try {
    const parsedSource = new URL(trimmedSource);
    const scheme = parsedSource.protocol.slice(0, -1).toLowerCase();
    if (scheme !== "http" && scheme !== "https") {
      return getNonNetworkImageSourceMetadata(scheme);
    }

    return {
      present: true,
      scheme,
      host: parsedSource.host,
      objectMarker: getStableObjectMarker(`${parsedSource.host}${parsedSource.pathname}`),
      hasQuery: Boolean(parsedSource.search),
      validUrl: true,
      urlWithoutCredentials: `${parsedSource.origin}${parsedSource.pathname}`,
    };
  } catch {
    return {
      present: true,
      scheme: null,
      host: null,
      objectMarker: getStableObjectMarker("invalid-url"),
      hasQuery: trimmedSource.includes("?"),
      validUrl: false,
      urlWithoutCredentials: null,
    };
  }
}

function useImageAssetOutcomeReporter(occlusionImageCardData: CardData) {
  const reportedImageOutcomesRef = useRef(new Set<string>());
  const imageSource = occlusionImageCardData?.question_side_image_url;
  const cardId = occlusionImageCardData?.id || occlusionImageCardData?.card_id || null;
  const deckId = occlusionImageCardData?.deck_id || null;

  return useCallback(
    (outcome: ImageAssetOutcome, currentSource?: string) => {
      const outcomeKey = `${cardId ?? "unknown-card"}:${imageSource ?? "missing-src"}:${outcome}`;
      if (reportedImageOutcomesRef.current.has(outcomeKey)) return;
      reportedImageOutcomesRef.current.add(outcomeKey);

      const sourceMetadata = getSafeImageSourceMetadata(imageSource);
      const currentSourceMetadata = getSafeImageSourceMetadata(currentSource);
      const diagnosticData = {
        cardId,
        deckId,
        outcome,
        sourcePresent: sourceMetadata.present,
        sourceScheme: sourceMetadata.scheme,
        sourceHost: sourceMetadata.host,
        sourceObjectMarker: sourceMetadata.objectMarker,
        sourceHasQuery: sourceMetadata.hasQuery,
        sourceUrlValid: sourceMetadata.validUrl,
        currentSrcPresent: currentSourceMetadata.present,
        currentSrcScheme: currentSourceMetadata.scheme,
        currentSrcHost: currentSourceMetadata.host,
        currentSrcObjectMarker: currentSourceMetadata.objectMarker,
        currentSrcHasQuery: currentSourceMetadata.hasQuery,
        currentSrcUrlValid: currentSourceMetadata.validUrl,
        currentSrc: currentSourceMetadata.urlWithoutCredentials,
      };

      if (outcome === "load") {
        addSentryDiagnosticBreadcrumb({
          category: IMAGE_OCCLUSION_ASSET_SCOPE,
          message: "Image occlusion asset loaded",
          data: diagnosticData,
        });
        return;
      }

      trackError(new Error(`Image occlusion asset ${outcome}`), {
        scope: IMAGE_OCCLUSION_ASSET_SCOPE,
        fingerprint: [IMAGE_OCCLUSION_ASSET_SCOPE, outcome],
        level: "warning",
        extra: diagnosticData,
      });
    },
    [cardId, deckId, imageSource]
  );
}

export function MissingImageOcclusionSourceDiagnostic({
  occlusionImageCardData,
}: {
  occlusionImageCardData: CardData;
}) {
  const imageSource = occlusionImageCardData?.question_side_image_url;
  const reportImageOutcome = useImageAssetOutcomeReporter(occlusionImageCardData);

  useEffect(() => {
    if (!imageSource?.trim()) {
      reportImageOutcome("missing-src");
    }
  }, [imageSource, reportImageOutcome]);

  return null;
}

export function ReviewQuestionSideImage({ currentCardData }: { currentCardData?: CardData }) {
  const imageDisplayUrl = useDisplayableImageUrl(currentCardData?.question_side_image_url);
  if (!currentCardData) return null;

  const imageSource = currentCardData.question_side_image_url;
  if (currentCardData.card_type === CardType.ImageOcclusion && !imageSource) {
    return <MissingImageOcclusionSourceDiagnostic occlusionImageCardData={currentCardData} />;
  }
  if (!imageSource) return null;

  return (
    <Box
      width="fit-content"
      height="auto"
      borderRadius="4px"
      margin="0 auto"
      marginBlock="32px"
      position="relative"
    >
      {currentCardData.occlusion_labels ? (
        <ImageAndLabelBoxesForOcclusionImage occlusionImageCardData={currentCardData} />
      ) : (
        <Image src={imageDisplayUrl} maxWidth="100%" borderRadius="12px" />
      )}
    </Box>
  );
}

// adds boxes that cover the labels for an occlusion image and label the number of the box
export default function ImageAndLabelBoxesForOcclusionImage({
  occlusionImageCardData,
  showAllLabels = false,
  shouldShowCurrentCardLabel = true,
}: {
  occlusionImageCardData: CardData;
  showAllLabels?: boolean;
  shouldShowCurrentCardLabel?: boolean;
}) {
  //local state
  const imageOcclusionRef = useRef<HTMLImageElement>(null);
  const [currentWidthOfImageForImageOcclusion, setCurrentWidthOfImageForImageOcclusion] = useState(
    imageOcclusionRef.current?.offsetWidth || 0
  );
  const [originalWidthOfImageForImageOcclusion, setOriginalWidthOfImageForImageOcclusion] = useState(
    imageOcclusionRef.current?.naturalWidth || 1
  );

  const [allLabelsRendered, setAllLabelsRendered] = useState(false);
  const [isImageReady, setIsImageReady] = useState(false); // Track when image is fully loaded

  //variables
  const occclusionImagelabels = occlusionImageCardData.occlusion_labels;
  const imageSource = occlusionImageCardData?.question_side_image_url;
  const imageDisplayUrl = useDisplayableImageUrl(imageSource);
  const reportImageOutcome = useImageAssetOutcomeReporter(occlusionImageCardData);

  useEffect(() => {
    if (!imageSource?.trim()) {
      reportImageOutcome("missing-src");
    }
  }, [imageSource, reportImageOutcome]);

  const handleImageDiagnosticLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    reportImageOutcome("load", event.currentTarget.currentSrc || event.currentTarget.src);
  };

  const handleImageDiagnosticError = (event: SyntheticEvent<HTMLImageElement>) => {
    reportImageOutcome("load-error", event.currentTarget.currentSrc || event.currentTarget.src);
  };

  // If the card created is before August 6, 2024, we need to have 560px as a reference
  let dateCreated = null;

  if (typeof occlusionImageCardData?.date_created === "string") {
    const dateString: string = occlusionImageCardData?.date_created as string;
    dateCreated = new Date(dateString);
  } else {
    dateCreated = new Date(occlusionImageCardData?.date_created.seconds * 1000);
  }

  // this is necessary because we need to calculate the size of the boxes based on the size of the image
  let referenceDateThatChangeSize = new Date(2024, 7, 27);

  let occlusionImageCardDataWidth = 0;
  if (dateCreated < referenceDateThatChangeSize) {
    occlusionImageCardDataWidth = 560;
  } else {
    occlusionImageCardDataWidth = MAX_WIDTH_FOR_ANSWER_AND_FEEDBACK_CONTENT;
  }

  let resizeFactor =
    currentWidthOfImageForImageOcclusion /
    (originalWidthOfImageForImageOcclusion >= occlusionImageCardDataWidth
      ? occlusionImageCardDataWidth
      : originalWidthOfImageForImageOcclusion);
  // Listen for changes in the width of the image occlusion for resizing the label boxes
  useEffect(() => {
    const imageOcclusionElement = imageOcclusionRef.current;

    if (imageOcclusionElement) {
      // Function to handle when image is loaded
      const handleImageLoad = () => {
        setOriginalWidthOfImageForImageOcclusion(imageOcclusionElement.naturalWidth);
        setIsImageReady(true);
      };

      // Check if image is already loaded
      if (imageOcclusionElement.complete && imageOcclusionElement.naturalWidth > 0) {
        handleImageLoad();
      } else {
        // Add event listener for when image loads
        imageOcclusionElement.addEventListener("load", handleImageLoad);
      }

      const imageOcclusionResizeObserver = new ResizeObserver(() => {
        setCurrentWidthOfImageForImageOcclusion(imageOcclusionElement.offsetWidth);
      });

      imageOcclusionResizeObserver.observe(imageOcclusionElement);

      return () => {
        imageOcclusionElement.removeEventListener("load", handleImageLoad);
        imageOcclusionResizeObserver.unobserve(imageOcclusionElement);
        imageOcclusionResizeObserver.disconnect();
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, [imageOcclusionRef.current, occclusionImagelabels]);
  useEffect(() => {
    if (occclusionImagelabels?.length) {
      setTimeout(() => {
        setAllLabelsRendered(true);
      }, 10);
    }
  }, [occclusionImagelabels]);

  return (
    <>
      {!allLabelsRendered && (
        <Box
          position="absolute"
          height="110%"
          width="110%"
          zIndex={1}
          backgroundColor={colors.surface.white}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Stack spacing={0} height="100%" width="100%" position="absolute" color="red">
            <Skeleton
              height="100%"
              width="100%"
              startColor={colors.surface.primary}
              endColor={colors.surface.secondary}
              borderRadius="12px"
            />
          </Stack>
        </Box>
      )}
      <Image
        ref={imageOcclusionRef}
        src={imageDisplayUrl}
        onLoad={handleImageDiagnosticLoad}
        onError={handleImageDiagnosticError}
        maxWidth="100%"
        borderRadius="12px"
      />
      {isImageReady &&
        occclusionImagelabels?.map((occlusionLabel, index) => {
          if (
            shouldShowCurrentCardLabel ||
            occlusionLabel.index + 1 !==
              (occlusionImageCardData.occlusion_label_index
                ? occlusionImageCardData.occlusion_label_index
                : occlusionImageCardData.occlusion_label_number)
          ) {
            const isLabelShownAsAskedAbout =
              !showAllLabels &&
              occlusionLabel.index + 1 ===
                (occlusionImageCardData.occlusion_label_index
                  ? occlusionImageCardData.occlusion_label_index
                  : occlusionImageCardData.occlusion_label_number);
            return (
              <Box
                key={index}
                height={`${occlusionLabel.height * (resizeFactor || 1)}px`}
                width={`${occlusionLabel.width * (resizeFactor || 1)}px`}
                left={`${occlusionLabel.x * (resizeFactor || 1)}px`}
                top={`${occlusionLabel.y * (resizeFactor || 1)}px`}
                backgroundColor={isLabelShownAsAskedAbout ? ASKED_ABOUT_LABEL_FILL : colors.secondaryAction.light}
                borderRadius="4px"
                border={`1px solid ${colors.secondaryAction.inactive}`}
                position="absolute"
                zIndex={
                  occlusionLabel.index + 1 ===
                  (occlusionImageCardData.occlusion_label_index
                    ? occlusionImageCardData.occlusion_label_index
                    : occlusionImageCardData.occlusion_label_number)
                    ? 100
                    : 0
                }
              >
                <Text
                  color={isLabelShownAsAskedAbout ? ASKED_ABOUT_LABEL_INK : colors.text.body}
                  fontSize={showAllLabels ? "12px" : "16px"}
                  fontWeight={
                    occlusionLabel.index + 1 ===
                    (occlusionImageCardData.occlusion_label_index
                      ? occlusionImageCardData.occlusion_label_index
                      : occlusionImageCardData.occlusion_label_number)
                      ? 800
                      : 500
                  }
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                >
                  {showAllLabels
                    ? occlusionLabel.index + 1
                    : occlusionLabel.index + 1 ===
                        (occlusionImageCardData.occlusion_label_index
                          ? occlusionImageCardData.occlusion_label_index
                          : occlusionImageCardData.occlusion_label_number) &&
                      occlusionImageCardData.occlusion_label_number}
                </Text>
              </Box>
            );
          }
          if (
            !shouldShowCurrentCardLabel &&
            occlusionLabel.index + 1 ===
              (occlusionImageCardData.occlusion_label_index
                ? occlusionImageCardData.occlusion_label_index
                : occlusionImageCardData.occlusion_label_number) &&
            occlusionLabel.answeredByUser
          ) {
            return (
              <Box
                key={index}
                height={`${occlusionLabel.height * (resizeFactor || 1)}px`}
                width={`${occlusionLabel.width * (resizeFactor || 1)}px`}
                left={`${occlusionLabel.x * (resizeFactor || 1)}px`}
                top={`${occlusionLabel.y * (resizeFactor || 1)}px`}
                position="absolute"
                zIndex={
                  occlusionLabel.index + 1 ===
                  (occlusionImageCardData.occlusion_label_index
                    ? occlusionImageCardData.occlusion_label_index
                    : occlusionImageCardData.occlusion_label_number)
                    ? 100
                    : 0
                }
              >
                <Text
                  borderRadius="4px"
                  color={colors.text.body}
                  fontSize={showAllLabels ? "12px" : "16px"}
                  fontWeight={
                    occlusionLabel.index + 1 ===
                    (occlusionImageCardData.occlusion_label_index
                      ? occlusionImageCardData.occlusion_label_index
                      : occlusionImageCardData.occlusion_label_number)
                      ? 800
                      : 500
                  }
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  padding="2px"
                >
                  {showAllLabels
                    ? occlusionLabel.index + 1
                    : occlusionLabel.index + 1 ===
                        (occlusionImageCardData.occlusion_label_index
                          ? occlusionImageCardData.occlusion_label_index
                          : occlusionImageCardData.occlusion_label_number) && occlusionLabel.labelText}
                </Text>
              </Box>
            );
          }

          return null;
        })}
    </>
  );
}
